const MIDDLEWARE_URL: string = import.meta.env.VITE_MIDDLEWARE_URL;

/*
    Example Strapi Error:

    {
        "data": null,
        "error": {
            "status": 400,
            "name": "ValidationError",
            "message": "This attribute must be unique",
            "details": {
            "errors": [
                {
                "path": [
                    "event_key"
                ],
                "message": "This attribute must be unique",
                "name": "ValidationError",
                "value": "u"
                }
            ]
            }
        }
    }
*/
export interface StrapiClientError {
  data?: (Record<string, never> | Record<string, never>[]) | null;
  error: {
    status?: number;
    name?: string;
    message?: string;
    details?: Record<string, never>;
  };
}

export interface StrapiDocumentRef {
  documentId: string;
}

export interface StrapiImage {
  id?: number;
  documentId?: string;
  name?: string;
  alternativeText?: string;
  caption?: string;
  width?: number;
  height?: number;
  formats?: unknown;
  hash?: string;
  ext?: string;
  mime?: string;
  /** Format: float */
  size?: number;
  url?: string;
  previewUrl?: string;
  provider?: string;
  provider_metadata?: unknown;
  related?: {
    id?: number;
    documentId?: string;
  }[];
  folderPath?: string;
}

export enum EventType {
  COMMUNITYDAY = "COMMUNITYDAY",
  CONFERENCE = "CONFERENCE",
  MEETUP = "MEETUP",
}

export enum EventStatus {
  DRAFT = "DRAFT",
  PUBLISHED = "PUBLISHED",
  ARCHIVE = "ARCHIVE",
}

export interface Event {
  documentId?: string;
  title: string;
  event_key?: string;
  /** Format: date-time */
  start?: string;
  /** Format: date-time */
  end?: string;
  max_participants?: number;
  event_type: EventType;
  sessions?: Session[];
  event_status?: EventStatus;
  description?: string;
  summary?: string;
  venue?: string;
  staff?: StrapiDocumentRef[];
  tickets?: Ticket[];
  workshopDiscount?: number;
  tickets_per_row?: number;
  optInRequired?: boolean;
}

export interface EmployeeRole {
  documentId?: string;
  role: string;
}

export interface Employee {
  documentId?: string;
  name: string;
  firstname: string;
  email: string;
  company?: string;
  image?: StrapiImage;
  jobtitle?: string;
  linkedin_url?: string;
  title?: string;
  resume?: string;
  roles?: EmployeeRole[];
}

export enum RegistrationStatus {
  OPEN = "OPEN",
  PAYED = "PAYED",
  CANCELED = "CANCELED",
}

export interface Registration {
  documentId?: string;
  uid?: string;
  company: string;
  address: string;
  zip: string;
  city: string;
  country?: string;
  registration_status?: RegistrationStatus;
  participants?: Participant[];
  ticket?: {
    id?: number;
    documentId?: string;
  };
}

export interface Participant {
  documentId?: string;
  title?: string;
  firstname: string;
  name: string;
  company?: string;
  jobtitle?: string;
  /** Format: email */
  email: string;
  phone?: string;
  newsletter?: boolean;
  authorize_data_transfer?: boolean;
  registration?: Registration;
}

export interface Session {
  documentId?: string;
  title: string;
  /** Format: date-time */
  start?: string;
  /** Format: date-time */
  end?: string;
  location?: string;
  session_type?: string;
  summary?: string;
  description?: string;
  speaker?: Employee[];
}

export enum PriceScaleType {
  FIXED_AMOUNT = "FIXED_AMOUNT",
  PERCENTAGE = "PERCENTAGE",
  SET_PRICE = "SET_PRICE"
};

export interface Ticket {
  documentId?: string;
  title: string;
  freeofcharge?: boolean;
  isWorkshop?: boolean;
  isKombiTicket?: boolean;
  min_participants?: number;
  max_participants?: number;
  price?: number;
  price_scale_type?: PriceScaleType;
  price_scale_value?: number;
  price_scale_type_2?: PriceScaleType;
  price_scale_value_2?: number;
  alt_price?: number;
  alt_price_positions?: number[]
  description?: string;
  /** Format: date-time */
  valid_from?: string;
  /** Format: date-time */
  valid_until?: string;
  event?: Event;
  registrations?: Registration[];
  confirmation_route?: string;
}

export interface Attendee {
  name?: string;
  firstname?: string;
  email?: string;
  company?: string;
  image?: string;
  jobtitle?: string;
  title?: string;
  phone?: string; //gibt es aktuell nur für Participants, würde bei Staff dann dementsprechend immer leer sein
}

export const getEvent = async (docId: string): Promise<any> => {
    try {
        const response = await fetch(`${MIDDLEWARE_URL}/events/${docId}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || "Network response was not ok");
        }

        return await response.json();
    } catch (error) {
        console.error("Error:", error);
    }
};

export const createRegistration = async (formData: any)=> {
    try {
        const response = await fetch(`${MIDDLEWARE_URL}/registrations`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: formData
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || "Network response was not ok");
        }

        return await response.json();
    } catch (error) {
        console.error("Error:", error);
    }
}